/**
 * Pure computation functions for the composition editor timeline tracks.
 * No DOM manipulation — each function takes data and returns plain descriptors.
 * The tracks controller is responsible for turning these into DOM.
 */
import {
  segmentOutputDuration,
  thumbnailUrl,
  segmentLabel,
  isPlayableVideo,
  isMediumMissing,
  segmentSlot,
  segmentMotion,
  segmentUrl,
  fallbackUrl,
  segmentError,
  trimSegment,
  trimOverlay,
  moveAudio,
  trimAudio,
  overlayContainers,
  nestedStates,
  moveOverlayTree,
  resolveSnaps,
} from "./composition_v3"
import type { Segment, Overlay, Composition, Audio } from "./composition_v3"
import { t } from "./i18n"
import { colorNameKey, isTransparent } from "./color_name"

interface Selection {
  kind: "segment" | "overlay" | "audio"
  index: number
}

// ==================== Ruler ====================

type TickType = "major" | "minor" | "quarter" | "sub"
type LabelAlign = "start" | "end" | "center"

export interface RulerTick {
  pct: number
  type: TickType
  label?: string
  align?: LabelAlign
}

export function computeRuler(totalDuration: number): RulerTick[] {
  if (totalDuration === 0) return []

  const major = totalDuration <= 20 ? 3 : Math.ceil(totalDuration / 5)
  const sub = totalDuration <= 20 ? 0.125 : 0.5
  const perSec = Math.round(1 / sub)
  const ticks: RulerTick[] = []

  for (let i = 0; i <= Math.round(totalDuration / sub); i++) {
    const tt = Math.round(i * sub * 1000) / 1000
    const pct = tt / totalDuration * 100

    if (i % (perSec * major) === 0) {
      const align: LabelAlign = i === 0 ? "start" : (pct > 99.5 ? "end" : "center")
      ticks.push({ pct, type: "major", label: `${tt}s`, align })
    } else if (i % perSec === 0) {
      ticks.push({ pct, type: "minor" })
    } else if (i % (perSec / 4) === 0) {
      ticks.push({ pct, type: "quarter" })
    } else {
      ticks.push({ pct, type: "sub" })
    }
  }

  return ticks
}

// ==================== Track: Media ====================

const GRADIENT_FALLBACK = "linear-gradient(135deg,#374151,#1f2937)"

interface ThumbnailColor { kind: "color"; background: string }
interface ThumbnailFrame { bg: string }
interface ThumbnailFrames { kind: "frames"; frames: ThumbnailFrame[] }
type Thumbnails = ThumbnailColor | ThumbnailFrames

function computeThumbnails(seg: Segment, width: number): Thumbnails {
  const n = Math.max(2, Math.round(width / 34))

  if (seg.type === "color") {
    return { kind: "color", background: seg.backgroundColor || "rgb(var(--color-surface_300))" }
  }

  if (seg.type === "image") {
    const url = thumbnailUrl(segmentUrl(seg), 0, "image")
    const bg = url ? `url('${url}'), ${GRADIENT_FALLBACK}` : GRADIENT_FALLBACK
    return { kind: "frames", frames: Array.from({ length: n }, () => ({ bg })) }
  }

  // video
  const frames: ThumbnailFrame[] = []
  for (let i = 0; i < n; i++) {
    const tLocal = seg.sourceStart + (i + 0.5) / n * (seg.sourceEnd - seg.sourceStart)
    const url = thumbnailUrl(segmentUrl(seg), tLocal)
    frames.push({ bg: url ? `url('${url}'), ${GRADIENT_FALLBACK}` : GRADIENT_FALLBACK })
  }
  return { kind: "frames", frames }
}

interface SegmentDescriptorBase {
  index: number
  id: string
  width: number
  insertIndex: number
  title: string
  background: string | null
}

export interface SegmentGenerating extends SegmentDescriptorBase {
  state: "generating" | "failed"
}

export interface SegmentPlaceholder extends SegmentDescriptorBase {
  state: "placeholder"
}

export interface SegmentReady extends SegmentDescriptorBase {
  state: "ready"
  selected: boolean
  icon: "video" | "photo"
  speed: number | null
  /** Le haut du range d'une séquence répétée (stade template), null sinon. */
  repeat: number | null
  /** L'expression du slot d'un fond lié (`media[i]`), null sinon. */
  slot: string | null
  thumbnails: Thumbnails
}

export type SegmentDescriptor = SegmentGenerating | SegmentPlaceholder | SegmentReady

export function computeMediaTrack(segments: Segment[], selection: Selection | null, pxPerSec: number): SegmentDescriptor[] {
  if (!segments.length) return []

  return segments.filter((seg: Segment) => !seg._trailing).map((seg: Segment, idx: number): SegmentDescriptor => {
    const outDur: number = segmentOutputDuration(seg)
    const width = Math.max(outDur * pxPerSec, 18)
    const label = segmentLabel(seg, idx)
    const base: SegmentDescriptorBase = {
      index: idx, id: `seg-${idx}`, width, insertIndex: idx,
      title: segmentSlot(seg) || label,
      background: fallbackUrl(seg),
    }

    if (isMediumMissing(seg)) {
      const error: string | null = segmentError(seg)
      return {
        ...base,
        state: error ? "failed" as const : "generating" as const,
        title: error ? `${error}\n${t("composition_editor.ai_plan_failed")}` : label,
      }
    }

    if (seg.placeholder) {
      return { ...base, state: "placeholder" as const }
    }

    const selected = selection?.kind === "segment" && selection.index === idx
    return {
      ...base,
      state: "ready" as const,
      selected,
      icon: seg.type === "video" ? "video" as const : "photo" as const,
      speed: (seg.speed && seg.speed !== 1) ? seg.speed : null,
      repeat: seg.repeat ? (seg.repeat.range?.[1] ?? null) : null,
      slot: segmentSlot(seg),
      thumbnails: computeThumbnails(seg, width),
    }
  })
}

// ==================== Track: Layers ====================

/** Un calque fabric tel que la piste le lit : son type et de quoi le nommer. */
interface LayerSnapshot {
  type?: string
  text?: string
  fill?: unknown
  stroke?: unknown
  bindings?: Record<string, { expr?: string, resolved?: boolean }>
  layout?: { container?: { arrangement?: string } }
}

export type LayerKind = "text" | "image" | "rect" | "circle" | "shape" | "line" | "group" | "block" | "layer"

/**
 * Le calque fabric d'un overlay : dans ses propres `_fabricLayers`, ou — enfant d'un
 * conteneur — dans ceux du conteneur, qui porte la copie sérialisée de sa descendance.
 */
function layerOf(overlays: Overlay[], overlay: Overlay): LayerSnapshot | undefined {
  const id = overlay._fabricId
  if (!id) return undefined
  for (const o of [overlay, ...overlays]) {
    const found = (o._fabricLayers as (LayerSnapshot & { layerId?: string })[] | undefined)?.find((l) => l.layerId === id)
    if (found) return found
  }
  return undefined
}

/** Ce qu'est un calque, à la place d'un nom technique (« Bloc 3 ») : Texte, Rectangle, Groupe… */
export function layerKind(layer: LayerSnapshot | undefined): LayerKind {
  const arrangement = layer?.layout?.container?.arrangement
  if (layer?.layout?.container) return arrangement === "free" ? "group" : "block"
  switch (layer?.type) {
    case "Textbox": case "IText": case "Text": return "text"
    case "ImageFrame": case "Image": return "image"
    case "Rect": return "rect"
    case "Circle": case "Ellipse": return "circle"
    case "Line": return "line"
    case "Path": case "Polygon": case "Triangle": return "shape"
    default: return "layer"
  }
}

/** Un champ lié à une variable (couleur selon l'annonce…) : sa valeur stockée ne dit rien. */
const bound = (layer: LayerSnapshot | undefined, field: string) => {
  const spec = layer?.bindings?.[field]
  return Boolean(spec && spec.resolved !== true)
}

/** Le texte d'un calque texte, sur une ligne : l'expression d'un texte lié (ses $TOKENS). */
function layerText(layer: LayerSnapshot | undefined): string {
  const raw = bound(layer, "text") ? layer?.bindings?.text?.expr : layer?.text
  return (raw || "").replace(/\s+/g, " ").trim()
}

/** Une image dynamique se nomme par son slot (`media[0]`, `$PICTURE_URL`) : c'est ce
 *  qu'elle sera, l'exemple affiché n'est qu'un aperçu. Elle ne prend pas de numéro. */
function imageSlot(layer: LayerSnapshot | undefined): string | null {
  return bound(layer, "image.src") ? layer?.bindings?.["image.src"]?.expr || null : null
}

/**
 * La couleur d'une forme : son fond, sinon son contour (une forme vide, un trait). Ni fond ni
 * contour qui peigne : « transparent ». Une couleur liée (selon l'annonce) ne se nomme pas.
 */
function layerColorName(layer: LayerSnapshot | undefined): string | null {
  if (bound(layer, "fill") || bound(layer, "stroke")) return null
  const key = colorNameKey(layer?.fill) || colorNameKey(layer?.stroke) ||
    (isTransparent(layer?.fill) && isTransparent(layer?.stroke) ? "transparent" : null)
  return key ? t(`composition_editor.layers.colors.${key}`, "") || null : null
}

// Un bloc ou un groupe a un fond : sa couleur le nomme aussi (transparent, rien à dire).
const COLORED_KINDS: LayerKind[] = ["rect", "circle", "shape", "line", "block", "group"]

/**
 * Le nom d'un calque, court — la colonne est étroite, il se tronque et l'infobulle le
 * donne en entier : le début du texte pour un texte, la forme et sa couleur pour une forme
 * (« Rectangle jaune »), « Image 2 » pour une image (numérotée dans l'ordre des lignes), son
 * slot pour une image dynamique.
 */
function layerNaming(layer: LayerSnapshot | undefined, kind: LayerKind, imageNumber: number): { label: string, tip: string } {
  const type = t(`composition_editor.layers.kinds.${kind}`, kind)
  if (kind === "text") {
    const text = layerText(layer)
    return text ? { label: text, tip: `${type} · ${text.slice(0, 120)}` } : { label: type, tip: type }
  }
  if (kind === "image") {
    const slot = imageSlot(layer)
    if (slot) return { label: slot, tip: `${type} · ${slot}` }
    const label = `${type} ${imageNumber}`
    return { label, tip: label }
  }
  const color = COLORED_KINDS.includes(kind) ? layerColorName(layer) : null
  const label = color ? `${type} ${color}` : type
  return { label, tip: label }
}

/** Ce qu'on sait d'un preset côté piste : son groupe et son nom affiché. */
export interface PresetInfo { group: string, label: string }

/** Une animation montrée sur la barre : à un bout, sur la largeur de sa durée. */
export interface AnimMark {
  side: "in" | "out"
  /** Largeur de la zone, en % de la barre (au plus la moitié). */
  width: number
  label: string
}

interface MarkedAnimation { preset?: string, fromEnd?: boolean, duration?: number | string }

/**
 * Les marques d'animation d'une barre : pour chaque côté, la plus longue — une entrée au
 * début de la présence, une sortie (`fromEnd`) à sa fin. On les lit sur la barre, on ne les
 * tire pas : leur timing se règle dans le panneau Animations. Les animations « continues »
 * (hors entrée et sortie) n'y figurent pas.
 */
export function computeAnimMarks(overlay: Overlay, presets: Record<string, PresetInfo> = {}): AnimMark[] {
  const life = Math.max(0.1, overlay.end - overlay.start)
  const marks: AnimMark[] = []
  for (const side of ["in", "out"] as const) {
    const anims = ((overlay.animations || []) as MarkedAnimation[]).filter((a) => {
      const group = a.preset ? presets[a.preset]?.group : undefined
      if (group === "continuous") return false
      return side === "out" ? Boolean(a.fromEnd) : !a.fromEnd
    })
    const longest = anims
      .map((a) => ({ a, d: Number(a.duration) || 0 }))
      .sort((x, y) => y.d - x.d)[0]
    if (!longest) continue
    marks.push({
      side,
      width: Math.min(50, Math.max(4, (longest.d / life) * 100)),
      label: (longest.a.preset && presets[longest.a.preset]?.label) || longest.a.preset || "",
    })
  }
  return marks
}

/** Le nombre d'animations d'entrée ou de sortie du document (la première posée a sa bulle). */
export function countAnimMarks(overlays: Overlay[], presets: Record<string, PresetInfo> = {}): number {
  return overlays.reduce((n, o) => n + computeAnimMarks(o, presets).length, 0)
}

export interface OverlayBarDescriptor {
  index: number
  /** Profondeur dans l'arbre des conteneurs : 0 = overlay libre ou conteneur. */
  depth: number
  /** Enfant d'un conteneur : sa fenêtre tient dans celle du parent, il bouge avec lui. */
  nested: boolean
  selected: boolean
  hidden: boolean
  kind: LayerKind
  /** Le nom du calque, court (cf. layerNaming). */
  label: string
  /** L'infobulle du nom : le nom en entier, le type devant pour un texte. */
  tip: string
  left: number
  width: number
  eyeIcon: "eye" | "eye-slash"
  marks: AnimMark[]
}

export interface OverlayGroupHeader {
  active: boolean
  chevron: "down" | "right"
  summaryText: string
}

export interface LayersDescriptor {
  header: OverlayGroupHeader
  bars: OverlayBarDescriptor[]
}

/**
 * La piste Calques : une ligne par bloc de la slide, nommée par ce qu'il est, la barre dit
 * quand il est dans la vidéo. `stackOrder` (les `_fabricId` du plus haut au plus bas du
 * canvas) range les lignes comme la slide : le bloc du dessus en haut. Sans lui, l'ordre du
 * document.
 */
export function computeLayersTrack(
  overlays: Overlay[], selection: Selection | null, totalDuration: number, groupOpen: boolean,
  stackOrder: string[] = [], presets: Record<string, PresetInfo> = {},
): LayersDescriptor | null {
  if (totalDuration === 0) return null

  const count = overlays.length
  const header: OverlayGroupHeader = {
    active: groupOpen,
    chevron: groupOpen ? "down" : "right",
    summaryText: t(count > 1 ? "composition_editor.layers.count_other" : "composition_editor.layers.count_one",
                   count > 1 ? "%{count} calques" : "%{count} calque").replace("%{count}", String(count)),
  }

  // Un conteneur masqué emporte ses enfants, comme au rendu.
  const states = nestedStates(overlays)
  let images = 0
  const bars: OverlayBarDescriptor[] = overlayTree(overlays, stackOrder).map(({ index: i, depth }) => {
    const overlay = overlays[i]
    const selected = selection?.kind === "overlay" && selection.index === i
    const isHidden = states[i].hidden
    const layer = layerOf(overlays, overlay)
    const kind = layerKind(layer)
    if (kind === "image" && !imageSlot(layer)) images += 1
    const { label, tip } = layerNaming(layer, kind, images)
    return {
      index: i,
      depth,
      nested: depth > 0,
      selected,
      hidden: isHidden,
      kind,
      label,
      tip,
      left: (overlay.start || 0) / totalDuration * 100,
      width: Math.min(overlay.end - overlay.start, totalDuration - overlay.start) / totalDuration * 100,
      eyeIcon: isHidden ? "eye-slash" as const : "eye" as const,
      marks: computeAnimMarks(overlay, presets),
    }
  })

  return { header, bars }
}

/**
 * L'ordre d'affichage des barres : chaque conteneur suivi de ses enfants (en profondeur),
 * frères rangés comme sur le canvas (le plus haut d'abord, cf. `stackOrder`), les autres
 * dans l'ordre du document. Un rattachement cyclique ou vers un conteneur absent retombe à
 * la racine — aucune barre ne disparaît.
 */
function overlayTree(overlays: Overlay[], stackOrder: string[] = []): { index: number, depth: number }[] {
  const containers = overlayContainers(overlays)
  const indexById = new Map<string, number>()
  overlays.forEach((o, i) => { if (o._fabricId) indexById.set(o._fabricId, i) })

  const rank = new Map(stackOrder.map((id, r) => [id, r]))
  const byStack = (a: number, b: number) =>
    (rank.get(overlays[a]._fabricId ?? "") ?? Infinity) - (rank.get(overlays[b]._fabricId ?? "") ?? Infinity) || a - b

  const children = new Map<number, number[]>()
  const roots: number[] = []
  overlays.forEach((o, i) => {
    const parent = o._fabricId ? indexById.get(containers.get(o._fabricId) ?? "") : undefined
    if (parent === undefined || parent === i) roots.push(i)
    else children.set(parent, [...(children.get(parent) || []), i])
  })

  const ordered: { index: number, depth: number }[] = []
  const seen = new Set<number>()
  const visit = (i: number, depth: number) => {
    if (seen.has(i)) return
    seen.add(i)
    ordered.push({ index: i, depth })
    for (const child of [...(children.get(i) || [])].sort(byStack)) visit(child, depth + 1)
  }
  ;[...roots].sort(byStack).forEach((i) => visit(i, 0))
  overlays.forEach((_, i) => visit(i, 0))
  return ordered
}

// ==================== Track: Audio ====================

export interface WaveformBar {
  height: number
  cls: string
}

function computeWaveform(selected: boolean): WaveformBar[] {
  const barCls = selected ? "bg-white/55" : "bg-surface_400"
  const bars: WaveformBar[] = []
  let v = 0.5, r = 7
  const rnd = () => { r = (r * 1103515245 + 12345) & 0x7fffffff; return r / 0x7fffffff }

  for (let i = 0; i < 110; i++) {
    v += (rnd() - 0.5) * 0.5
    v = Math.max(0.15, Math.min(1, v))
    const ramp = i < 9 ? (i + 1) / 9 : i >= 101 ? (110 - i) / 9 : 1
    bars.push({
      height: Math.round((v * 70 + 14) * (0.2 + 0.8 * ramp)),
      cls: barCls,
    })
  }
  return bars
}

export interface AudioDescriptor {
  selected: boolean
  left: number
  width: number
  name: string
  waveformBars: WaveformBar[]
  /** Stade template : le nombre de candidats — la bande les nomme, sans poignées ni onde. */
  candidates?: number
}

/** La bande des candidats d'un template : son titre s'il n'y en a qu'un, leur nombre sinon. */
function candidatesLabel(candidates: Audio["candidates"] & object): string {
  if (candidates.length === 1) return candidates[0].title || t("composition_editor.audio.title", "Musique")
  return t("composition_editor.audio.selected_other", "%{count} morceaux sélectionnés")
    .replace("%{count}", String(candidates.length))
}

export function computeAudioTrack(audio: Audio, selection: Selection | null, totalDuration: number): AudioDescriptor | null {
  if (totalDuration === 0) return null
  if (!audio?.src && audio?.candidates?.length) {
    return { selected: false, left: 0, width: 100, name: candidatesLabel(audio.candidates), waveformBars: [],
             candidates: audio.candidates.length }
  }
  if (!audio?.src) return null

  const selected = selection?.kind === "audio"
  const start = audio.offset || 0
  const end = audio.duration != null ? start + audio.duration : totalDuration
  return {
    selected,
    left: Math.max(0, start / totalDuration * 100),
    width: Math.max(0, Math.min(100 - Math.max(0, start / totalDuration * 100), (end - start) / totalDuration * 100)),
    name: audio.name || "Musique",
    waveformBars: computeWaveform(selected),
  }
}

// ==================== Drag interactions ====================

type Handle = "L" | "R"

interface SegmentTrimCallbacks {
  onMove: (seg: Segment, ev: PointerEvent) => void
  onEnd: () => void
}

export function startSegmentTrim(
  composition: Composition, segIndex: number, handle: Handle,
  e: PointerEvent, pps: number, { onMove, onEnd }: SegmentTrimCallbacks,
): void {
  const seg = composition.segments[segIndex]
  if (seg.placeholder) return
  e.preventDefault()
  const x0 = e.clientX
  const initStart = seg.sourceStart ?? 0
  const initEnd = seg.sourceEnd ?? initStart + (seg.duration || 0)
  const initDuration = seg.duration

  const move = (ev: PointerEvent) => {
    const d = (ev.clientX - x0) / pps
    if (isPlayableVideo(seg)) {
      seg.sourceStart = initStart
      seg.sourceEnd = initEnd
    } else {
      seg.duration = initDuration
    }
    trimSegment(composition, segIndex, handle, d)
    onMove(seg, ev)
  }
  const up = () => {
    document.removeEventListener("pointermove", move)
    document.removeEventListener("pointerup", up)
    onEnd()
  }
  document.addEventListener("pointermove", move as EventListener)
  document.addEventListener("pointerup", up)
}

interface OverlayDragCallbacks {
  onMove: () => void
  onDragEnd: () => void
  onClickEnd: () => void
}

// Un conteneur déplacé emporte ses enfants ; rogné, il les reborne (resolveSnaps) — un
// enfant déplacé seul reste dans son parent.
export function startOverlayDrag(
  composition: Composition, overlay: Overlay, handle: Handle | null, e: PointerEvent,
  pps: number, totalDuration: number, { onMove, onDragEnd, onClickEnd }: OverlayDragCallbacks,
): void {
  e.preventDefault()
  const x0 = e.clientX
  const s0 = overlay.start
  const d0 = overlay.end - overlay.start
  const DRAG_THRESHOLD = 4
  let dragging = false

  const move = (ev: PointerEvent) => {
    const dx = ev.clientX - x0
    if (!dragging && Math.abs(dx) < DRAG_THRESHOLD) return
    dragging = true
    const d = dx / pps
    if (handle) {
      trimOverlay(overlay, handle, handle === "L" ? s0 + d : s0 + d0 + d)
    } else {
      moveOverlayTree(composition, overlay, s0 + d, totalDuration)
    }
    resolveSnaps(composition)
    onMove()
  }
  const up = () => {
    document.removeEventListener("pointermove", move)
    document.removeEventListener("pointerup", up)
    if (dragging) onDragEnd()
    else onClickEnd()
  }
  document.addEventListener("pointermove", move as EventListener)
  document.addEventListener("pointerup", up)
}

interface AudioDragCallbacks {
  onMove: () => void
  onEnd: () => void
}

export function startAudioDrag(
  audio: Audio, handle: Handle | null, e: PointerEvent,
  pps: number, totalDuration: number, { onMove, onEnd }: AudioDragCallbacks,
): void {
  e.preventDefault()
  const x0 = e.clientX
  const start0 = audio.offset || 0
  const dur0 = audio.duration != null ? audio.duration : totalDuration - start0
  let moved = false

  const move = (ev: PointerEvent) => {
    if (!moved) {
      if (Math.abs(ev.clientX - x0) < 3) return
      moved = true
    }
    const d = (ev.clientX - x0) / pps
    if (handle === "L") trimAudio(audio, "L", start0 + d, totalDuration)
    else if (handle === "R") trimAudio(audio, "R", start0 + dur0 + d, totalDuration)
    else moveAudio(audio, start0 + d, totalDuration)
    onMove()
  }
  const up = () => {
    document.removeEventListener("pointermove", move)
    document.removeEventListener("pointerup", up)
    if (moved) onEnd()
  }
  document.addEventListener("pointermove", move as EventListener)
  document.addEventListener("pointerup", up)
}

// ==================== Contextual toolbar ====================

export interface CtxState {
  visible: boolean
  showSpeed?: boolean
  speedTip?: string
  hasSpeed?: boolean
  speedText?: string
  hasRepeat?: boolean
  repeatText?: string
  repeatRange?: [number, number]
  showMotion?: boolean
  motion?: string | null
  /** Un plan IA échoué : le geste Relancer, sur cette séquence. */
  showRetry?: boolean
  retrySegmentId?: string
}

export function computeCtxState(segment: Segment | null): CtxState {
  if (!segment) return { visible: false }

  const result: CtxState = { visible: true }

  result.showRetry = isMediumMissing(segment) && !!segmentError(segment)
  if (result.showRetry) result.retrySegmentId = segment.id

  if (isPlayableVideo(segment)) {
    result.showSpeed = true
    result.speedTip = `Vitesse · ${segment.speed || 1}x`
    result.hasSpeed = !!(segment.speed && segment.speed !== 1)
    if (result.hasSpeed) result.speedText = `${segment.speed}x`
  } else {
    result.showSpeed = false
  }

  result.showMotion = segment.type === "image"
  result.motion = result.showMotion ? segmentMotion(segment) : null

  result.hasRepeat = !!segment.repeat
  if (segment.repeat) {
    const [min, max] = segment.repeat.range || [1, 8]
    result.repeatRange = [min, max]
    result.repeatText = `×${min}–${max}`
  }

  return result
}
